import { DebugLogger } from '@affine/debug';
import { Unreachable } from '@affine/env/constant';
import { Permission, updateDocTitleMutation } from '@affine/graphql';
import { replaceIdMiddleware } from '@blocksuite/affine/shared/adapters';
import type { AffineTextAttributes } from '@blocksuite/affine/shared/types';
import type { DeltaInsert, DocMeta } from '@blocksuite/affine/store';
import { Slice, Text, Transformer } from '@blocksuite/affine/store';
import { LiveData, ObjectPool, Service } from '@toeverything/infra';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

import { initDocFromProps } from '../../../blocksuite/initialization';
import {
  isBackendKnownUnavailable,
  probeBackendAvailabilityNow,
} from '../../../utils/backend-availability';
import { FetchService } from '../../cloud/services/fetch';
import type { WorkspaceServerService } from '../../cloud/services/workspace-server';
import type { WorkspacePermissionService } from '../../permissions/services/permission';
import type { WorkspaceService } from '../../workspace';
import { getStreamLainWorkspaceSchema } from '../../workspace';
import { recordWorkspaceProjectionDiagnostic } from '../../workspace-projection-reconciliation/diagnostics';
import type { Doc } from '../entities/doc';
import { DocRecordList } from '../entities/record-list';
import { DocCreated, DocInitialized } from '../events';
import type { DocCreateMiddleware } from '../providers/doc-create-middleware';
import { DocScope } from '../scopes/doc';
import type { DocPropertiesStore } from '../stores/doc-properties';
import type { DocsStore } from '../stores/docs';
import type { DocCreateOptions } from '../types';
import { DocService } from './doc';

const INITIAL_TARGET_FOLDER_ID_META_KEY = 'streamlain:initialTargetFolderId';

type PendingFunnelCoreJob = {
  accountId: string;
  serverId: string;
  serverOrigin: string;
  workspaceId: string;
  docId: string;
  type: 'document_created';
  expiresAt: number;
};

type FunnelCoreJobEligibilityIntent = Omit<
  PendingFunnelCoreJob,
  'expiresAt'
> & {
  retryUntil: number;
};

const FUNNEL_CORE_JOB_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const logger = new DebugLogger('DocsService');

export const CREATE_DOC_PERMISSION_ERROR =
  'Workspace Reader cannot create documents';

export function isCreateDocPermissionError(error: unknown): boolean {
  return (
    error instanceof Error && error.message === CREATE_DOC_PERMISSION_ERROR
  );
}

export class DocsService extends Service {
  private static readonly FUNNEL_CORE_JOB_PENDING_KEY =
    'streamlain:funnel-core-job-pending:v2';
  private readonly funnelCoreJobRetryTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  list = this.framework.createEntity(DocRecordList);

  private readonly loadedDocIdsSubject$ = new BehaviorSubject<
    readonly string[]
  >([]);

  /**
   * Documents with an explicitly retained DocScope.
   *
   * Metadata consumers use this working-set signal instead of opening every
   * workspace record. Opening a document is a content-sync request and never
   * a valid discovery primitive.
   */
  readonly loadedDocIds$ = LiveData.from(
    this.loadedDocIdsSubject$.asObservable(),
    [] as readonly string[]
  );

  pool = new ObjectPool<string, Doc>({
    onDelete: obj => {
      obj.scope.dispose();
    },
    onChange: docIds => this.loadedDocIdsSubject$.next(docIds),
  });

  /**
   * Get all property values of a property, used for search
   *
   * Results may include docs in trash or deleted docs
   * Legacy property data such as old `journal` will not be included in the values
   */
  propertyValues$(propertyKey: string) {
    return combineLatest([
      this.store.watchDocIds(),
      this.docPropertiesStore.watchPropertyAllValues(propertyKey),
    ]).pipe(
      map(([docIds, propertyValues]) => {
        const result = new Map<string, string | undefined>();
        for (const docId of docIds) {
          result.set(docId, propertyValues.get(docId));
        }
        return result;
      })
    );
  }

  /**
   * used for search
   */
  allDocsCreatedDate$() {
    return this.store.watchAllDocCreateDate();
  }

  /**
   * used for search
   */
  allDocsUpdatedDate$() {
    return this.store.watchAllDocUpdatedDate();
  }

  allDocsTagIds$() {
    return this.store.watchAllDocTagIds();
  }

  allDocIds$() {
    return this.store.watchDocIds();
  }

  allNonTrashDocIds$() {
    return this.store.watchNonTrashDocIds();
  }

  allTrashDocIds$() {
    return this.store.watchTrashDocIds();
  }

  allDocTitle$() {
    return this.store.watchAllDocTitle();
  }

  constructor(
    private readonly store: DocsStore,
    private readonly docPropertiesStore: DocPropertiesStore,
    private readonly docCreateMiddlewares: DocCreateMiddleware[],
    private readonly workspaceService: WorkspaceService,
    private readonly workspacePermissionService: WorkspacePermissionService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
    if (this.workspaceService.workspace.flavour !== 'local') {
      let confirmedAccountSubscription: { unsubscribe(): void } | undefined;
      let cachedAccountSubscription: { unsubscribe(): void } | undefined;
      const bindAccountSubscription = () => {
        confirmedAccountSubscription?.unsubscribe();
        cachedAccountSubscription?.unsubscribe();
        confirmedAccountSubscription =
          this.workspaceServerService.server?.confirmedAccountId$.subscribe(
            () => void this.retryPersistedFunnelCoreJob()
          );
        cachedAccountSubscription =
          this.workspaceServerService.server?.account$.subscribe(
            () => void this.retryPersistedFunnelCoreJob()
          );
      };
      const serverSubscription = this.workspaceServerService.server$.subscribe(
        bindAccountSubscription
      );
      bindAccountSubscription();
      this.disposables.push(() => serverSubscription.unsubscribe());
      this.disposables.push(() => confirmedAccountSubscription?.unsubscribe());
      this.disposables.push(() => cachedAccountSubscription?.unsubscribe());
      queueMicrotask(() => void this.retryPersistedFunnelCoreJob());
    }
  }

  private assertCanCreateDoc() {
    if (
      this.workspaceService.workspace.flavour === 'local' ||
      this.workspaceService.workspace.openOptions.isSharedMode
    ) {
      return;
    }

    if (
      this.workspacePermissionService.permission.role$.value ===
      Permission.Reader
    ) {
      throw new Error(CREATE_DOC_PERMISSION_ERROR);
    }
  }

  /**
   * Check if a doc was created locally in this session (creator = current user).
   * Used by DetailPageWrapper to bypass async permission checks.
   */
  isLocallyCreated(docId: string): boolean {
    return this.store.isLocallyCreated(docId);
  }

  discardLocalDoc(docId: string): Promise<void> {
    return this.store.discardLocalDoc(docId);
  }

  waitForServerConfirmed(
    docId: string,
    options?: { timeoutMs?: number; pollMs?: number }
  ): Promise<boolean> {
    return this.store.waitForServerConfirmed(docId, options);
  }

  /**
   * Flush a locally-created cloud doc and its workspace projection before
   * treating it as available to another workspace member.
   *
   * A Yjs sync acknowledgement only proves that the local update reached the
   * sync pipeline. Cross-user actions also require the server-side document
   * and inherited-permission projection to exist.
   */
  async waitForLocalDocServerConfirmed(
    docId: string,
    options: { timeoutMs?: number; pollMs?: number } = {}
  ): Promise<boolean> {
    const workspace = this.workspaceService.workspace;
    if (workspace.flavour === 'local') {
      return true;
    }
    if (!this.store.isLocallyCreated(docId)) {
      return this.store.waitForServerConfirmed(docId, options);
    }

    const timeoutMs = options.timeoutMs ?? 30_000;
    const deadline = Date.now() + timeoutMs;
    await this.flushLocalDocBeforeMetadataWrite(
      docId,
      AbortSignal.timeout(timeoutMs)
    );
    const remainingTimeoutMs = deadline - Date.now();
    if (remainingTimeoutMs <= 0) {
      return false;
    }
    return this.store.waitForServerConfirmed(docId, {
      timeoutMs: remainingTimeoutMs,
      pollMs: options.pollMs ?? 250,
    });
  }

  ensureDocCached(
    docId: string,
    options?: { signal?: AbortSignal }
  ): Promise<boolean> {
    return this.store.ensureDocCached(docId, options);
  }

  /**
   * Notify the permission cache that a doc was created.
   * Use this when docs are created outside DocsService.createDoc()
   * (e.g. ZipTransformer.importDocs) to ensure accessibleDocCache
   * knows the doc exists and DocsService.list.doc$() returns it.
   */
  notifyDocCreated(docId: string, initialMeta?: Partial<DocMeta>): void {
    this.store.notifyDocCreated(docId, initialMeta);
  }

  loaded(docId: string) {
    const exists = this.pool.get(docId);
    if (exists) {
      return { doc: exists.obj, release: exists.release };
    }
    return null;
  }

  open(docId: string, options: { connectAwareness?: boolean } = {}) {
    const docRecord = this.list.doc$(docId).value;
    if (!docRecord) {
      throw new Error('Doc record not found');
    }
    const blockSuiteDoc = this.store.getBlockSuiteDoc(docId);
    if (!blockSuiteDoc) {
      throw new Error('Doc not found');
    }

    // Opening is the authoritative on-demand signal for selective sync. A doc
    // may already be pooled after metadata hydration, without ever having
    // completed a remote connect job.
    this.workspaceService.workspace.engine.doc.addDoc(docId, 'doc-open');

    const exists = this.pool.get(docId);
    if (exists) {
      if (options.connectAwareness) {
        exists.obj.blockSuiteDoc.doc.connectAwareness();
      }
      return { doc: exists.obj, release: exists.release };
    }

    const docScope = this.framework.createScope(DocScope, {
      docId,
      blockSuiteDoc,
      record: docRecord,
    });

    try {
      blockSuiteDoc.load();
      if (options.connectAwareness) {
        blockSuiteDoc.doc.connectAwareness();
      }
    } catch (e) {
      logger.error('Failed to load doc', {
        docId,
        error: e,
      });
    }

    const doc = docScope.get(DocService).doc;

    doc.scope.emitEvent(DocInitialized, doc);

    const { obj, release } = this.pool.put(docId, doc);

    return { doc: obj, release };
  }

  createDoc(options: DocCreateOptions = {}) {
    this.assertCanCreateDoc();
    for (const middleware of this.docCreateMiddlewares) {
      options = middleware.beforeCreate
        ? middleware.beforeCreate(options)
        : options;
    }
    const id = this.store.createDoc(options.id, options.targetFolderId);
    const docStore = this.store.getBlockSuiteDoc(id);
    if (!docStore) {
      throw new Error('Failed to create doc');
    }
    if (options.targetFolderId) {
      // registerDoc() owns a lightweight DocImpl before getStore() creates the
      // provider-backed Store. Seed that exact Store document as well, before
      // load/init can yield to the initial sync handshake. Seeding only the
      // pre-Store DocImpl can otherwise publish a first snapshot without the
      // target and temporarily materialize the Document at workspace root.
      docStore.spaceDoc
        .getMap('meta')
        .set(INITIAL_TARGET_FOLDER_ID_META_KEY, options.targetFolderId);
    }
    if (options.skipInit !== true) {
      initDocFromProps(docStore, options.docProps, options);
    }
    this.store.markDocCreating(id);
    for (const middleware of this.docCreateMiddlewares) {
      middleware.beforeLocalVisibility?.(id, options);
    }
    // Notify permission cache AFTER blocks are initialized.
    // This triggers LiveData → React re-render → DetailPageWrapper mount.
    // The doc must have its root block and folder placement before that happens.
    this.store.notifyDocCreated(
      id,
      options.title ? { title: options.title } : undefined
    );
    const docRecord = this.list.doc$(id).value;
    if (!docRecord) {
      throw new Unreachable();
    }
    if (options.primaryMode) {
      docRecord.setPrimaryMode(options.primaryMode);
    }
    if (options.isTemplate) {
      docRecord.setProperty('isTemplate', true);
    }
    for (const middleware of this.docCreateMiddlewares) {
      middleware.afterCreate?.(docRecord, options);
    }
    docRecord.setCreatedAt(Date.now());
    docRecord.setUpdatedAt(Date.now());
    this.eventBus.emit(DocCreated, {
      doc: docRecord,
      docCreateOptions: options,
    });
    this.scheduleInitialTargetFolderConfirmation(id, options);
    this.scheduleFunnelCoreJobConfirmation(id, options);
    return docRecord;
  }

  private scheduleFunnelCoreJobConfirmation(
    docId: string,
    options: DocCreateOptions
  ): void {
    if (
      !options.funnelCoreJobType ||
      this.workspaceService.workspace.flavour === 'local'
    ) {
      return;
    }
    const { release } = this.open(docId);
    void this.postFunnelCoreJobWithRetry(docId, options.funnelCoreJobType)
      .catch(() => {
        // Optional analytics never affects document creation. A later explicit
        // document create retries the same backend-idempotent milestone.
      })
      .finally(release);
  }

  private async postFunnelCoreJobWithRetry(
    docId: string,
    coreJobType: NonNullable<DocCreateOptions['funnelCoreJobType']>,
    retryUntil = Date.now() + 10 * 60 * 1000,
    existingIntent?: FunnelCoreJobEligibilityIntent
  ): Promise<void> {
    const intent =
      existingIntent ??
      this.createFunnelCoreJobEligibilityIntent(docId, coreJobType, retryUntil);
    if (!intent) return;
    const identityState = this.funnelCoreJobIntentIdentityState(intent);
    if (identityState === 'mismatch') return;
    if (identityState === 'pending') {
      this.scheduleFunnelCoreJobEligibilityRetry(intent);
      return;
    }
    const eligibility = await this.funnelCoreJobEligibility();
    if (eligibility === 'unavailable') {
      this.scheduleFunnelCoreJobEligibilityRetry(intent);
      return;
    }
    if (!eligibility) return;
    const pending = this.persistFunnelCoreJob(intent);
    if (pending) await this.confirmAndSendFunnelCoreJob(pending);
  }

  private async retryPersistedFunnelCoreJob(): Promise<void> {
    for (const pending of this.readPendingFunnelCoreJobs()) {
      if (this.matchesFunnelCoreJobIdentity(pending)) {
        await this.confirmAndSendFunnelCoreJob(pending);
      }
    }
  }

  private createFunnelCoreJobEligibilityIntent(
    docId: string,
    coreJobType: NonNullable<DocCreateOptions['funnelCoreJobType']>,
    retryUntil: number
  ): FunnelCoreJobEligibilityIntent | undefined {
    const server = this.workspaceServerService.server;
    const accountId = server?.confirmedAccountId ?? server?.cachedAccountId;
    if (server && accountId) {
      return {
        accountId,
        serverId: server.id,
        serverOrigin: new URL(server.baseUrl).origin,
        workspaceId: this.workspaceService.workspace.id,
        docId,
        type: coreJobType,
        retryUntil,
      };
    }
    return undefined;
  }

  private funnelCoreJobIntentIdentityState(
    intent: FunnelCoreJobEligibilityIntent
  ): 'confirmed' | 'pending' | 'mismatch' {
    const server = this.workspaceServerService.server;
    if (
      !server ||
      server.id !== intent.serverId ||
      new URL(server.baseUrl).origin !== intent.serverOrigin ||
      this.workspaceService.workspace.id !== intent.workspaceId
    ) {
      return 'mismatch';
    }
    if (server.confirmedAccountId) {
      return server.confirmedAccountId === intent.accountId
        ? 'confirmed'
        : 'mismatch';
    }
    return server.cachedAccountId === intent.accountId ? 'pending' : 'mismatch';
  }

  private persistFunnelCoreJob(
    intent: FunnelCoreJobEligibilityIntent
  ): PendingFunnelCoreJob | undefined {
    if (typeof localStorage !== 'undefined') {
      const { retryUntil: _, ...identity } = intent;
      const candidate: PendingFunnelCoreJob = {
        ...identity,
        expiresAt: Date.now() + FUNNEL_CORE_JOB_TTL_MS,
      };
      const pending = this.readPendingFunnelCoreJobs();
      const existing = pending.find(item =>
        this.sameFunnelCoreJobIdentity(item, candidate)
      );
      if (existing) return existing;
      pending.push(candidate);
      return this.writePendingFunnelCoreJobs(pending) ? candidate : undefined;
    }
    return undefined;
  }

  private async confirmAndSendFunnelCoreJob(
    pending: PendingFunnelCoreJob
  ): Promise<void> {
    if (!this.matchesFunnelCoreJobIdentity(pending)) return;
    if (!(await this.waitForLocalDocServerConfirmed(pending.docId))) {
      this.scheduleFunnelCoreJobRetry(pending);
      return;
    }
    const eligibility = await this.funnelCoreJobEligibility();
    if (eligibility === 'unavailable') {
      this.scheduleFunnelCoreJobRetry(pending);
      return;
    }
    if (!eligibility) {
      this.removePendingFunnelCoreJob(pending);
      return;
    }
    const retryDelays = [0, 1_000, 3_000, 10_000, 30_000, 60_000];
    for (const delay of retryDelays) {
      if (delay > 0) await new Promise(resolve => setTimeout(resolve, delay));
      const server = this.workspaceServerService.server;
      if (!server || !this.matchesFunnelCoreJobIdentity(pending)) return;
      try {
        const response = await server.scope
          .get(FetchService)
          .fetch('/api/funnel/core-job', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ core_job_type: pending.type }),
          });
        if (response.ok || response.status === 400 || response.status === 422) {
          this.removePendingFunnelCoreJob(pending);
          return;
        }
      } catch {
        // Retry transient availability failures; backend storage is idempotent.
      }
    }
    this.scheduleFunnelCoreJobRetry(pending);
  }

  private matchesFunnelCoreJobIdentity(pending: PendingFunnelCoreJob): boolean {
    const server = this.workspaceServerService.server;
    if (!server) return false;
    return (
      server.confirmedAccountId === pending.accountId &&
      server.id === pending.serverId &&
      new URL(server.baseUrl).origin === pending.serverOrigin &&
      this.workspaceService.workspace.id === pending.workspaceId
    );
  }

  private sameFunnelCoreJobIdentity(
    left: PendingFunnelCoreJob,
    right: PendingFunnelCoreJob
  ): boolean {
    return (
      left.accountId === right.accountId &&
      left.serverId === right.serverId &&
      left.serverOrigin === right.serverOrigin &&
      left.workspaceId === right.workspaceId &&
      left.type === right.type
    );
  }

  private async funnelCoreJobEligibility(): Promise<boolean | 'unavailable'> {
    const server = this.workspaceServerService.server;
    if (!server?.confirmedAccountId) return 'unavailable';
    try {
      const response = await server.scope
        .get(FetchService)
        .fetch('/api/funnel/eligibility', { cache: 'no-store' });
      if (!response.ok) return 'unavailable';
      const body = (await response.json()) as { enabled?: unknown };
      return body.enabled === true;
    } catch {
      return 'unavailable';
    }
  }

  private scheduleFunnelCoreJobRetry(pending: PendingFunnelCoreJob): void {
    if (pending.expiresAt <= Date.now()) {
      this.removePendingFunnelCoreJob(pending);
      return;
    }
    const key = this.funnelCoreJobIdentityKey(pending);
    if (this.funnelCoreJobRetryTimers.has(key)) return;
    const timer = setTimeout(() => {
      this.funnelCoreJobRetryTimers.delete(key);
      void this.confirmAndSendFunnelCoreJob(pending);
    }, 30_000);
    this.funnelCoreJobRetryTimers.set(key, timer);
  }

  private scheduleFunnelCoreJobEligibilityRetry(
    intent: FunnelCoreJobEligibilityIntent
  ): void {
    if (intent.retryUntil <= Date.now()) return;
    const key = `eligibility\u0000${this.funnelCoreJobIdentityKey(intent)}`;
    if (this.funnelCoreJobRetryTimers.has(key)) return;
    const timer = setTimeout(() => {
      this.funnelCoreJobRetryTimers.delete(key);
      void this.postFunnelCoreJobWithRetry(
        intent.docId,
        intent.type,
        intent.retryUntil,
        intent
      );
    }, 30_000);
    this.funnelCoreJobRetryTimers.set(key, timer);
  }

  private funnelCoreJobIdentityKey(
    pending: PendingFunnelCoreJob | FunnelCoreJobEligibilityIntent
  ): string {
    return [
      pending.accountId,
      pending.serverId,
      pending.serverOrigin,
      pending.workspaceId,
      pending.type,
    ].join('\u0000');
  }

  private readPendingFunnelCoreJobs(): PendingFunnelCoreJob[] {
    if (typeof localStorage === 'undefined') return [];
    try {
      const raw = localStorage.getItem(DocsService.FUNNEL_CORE_JOB_PENDING_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) throw new Error('Invalid funnel queue');
      const now = Date.now();
      const valid = parsed.filter(
        (value): value is PendingFunnelCoreJob =>
          typeof value === 'object' &&
          value !== null &&
          (value as PendingFunnelCoreJob).type === 'document_created' &&
          typeof (value as PendingFunnelCoreJob).accountId === 'string' &&
          typeof (value as PendingFunnelCoreJob).serverId === 'string' &&
          typeof (value as PendingFunnelCoreJob).serverOrigin === 'string' &&
          typeof (value as PendingFunnelCoreJob).workspaceId === 'string' &&
          typeof (value as PendingFunnelCoreJob).docId === 'string' &&
          typeof (value as PendingFunnelCoreJob).expiresAt === 'number' &&
          (value as PendingFunnelCoreJob).expiresAt > now
      );
      if (valid.length !== parsed.length)
        this.writePendingFunnelCoreJobs(valid);
      return valid;
    } catch {
      try {
        localStorage.removeItem(DocsService.FUNNEL_CORE_JOB_PENDING_KEY);
      } catch {
        // Storage is optional; fail closed without emitting analytics.
      }
      return [];
    }
  }

  private writePendingFunnelCoreJobs(items: PendingFunnelCoreJob[]): boolean {
    if (typeof localStorage === 'undefined') return false;
    try {
      if (items.length === 0) {
        localStorage.removeItem(DocsService.FUNNEL_CORE_JOB_PENDING_KEY);
      } else {
        localStorage.setItem(
          DocsService.FUNNEL_CORE_JOB_PENDING_KEY,
          JSON.stringify(items)
        );
      }
      return true;
    } catch {
      return false;
    }
  }

  private removePendingFunnelCoreJob(pending: PendingFunnelCoreJob): void {
    this.writePendingFunnelCoreJobs(
      this.readPendingFunnelCoreJobs().filter(
        item => !this.sameFunnelCoreJobIdentity(item, pending)
      )
    );
  }

  private scheduleInitialTargetFolderConfirmation(
    docId: string,
    options: DocCreateOptions
  ): void {
    if (
      !options.targetFolderId ||
      this.workspaceService.workspace.flavour === 'local'
    ) {
      return;
    }

    // Keep the newly-created DocScope alive until its first local/server sync
    // handshake completes. Without this retain, immediate navigation can
    // dispose the provider before the Y.Doc reaches local storage, leaving the
    // sync engine with metadata for a doc whose content cannot be pushed.
    const { release } = this.open(docId);
    void this.confirmInitialTargetFolderCreate(docId, options)
      .catch(error => {
        logger.error('Failed to verify initial target-folder doc create', {
          docId,
          targetFolderId: options.targetFolderId,
          error,
        });
      })
      .finally(release);
  }

  private async confirmInitialTargetFolderCreate(
    docId: string,
    options: DocCreateOptions
  ): Promise<void> {
    const docEngine = this.workspaceService.workspace.engine?.doc;
    const workspaceId = this.workspaceService.workspace.id;
    const releaseDocPriority = docEngine?.addPriority(docId, 200);
    const releaseWorkspacePriority = docEngine?.addPriority(workspaceId, 100);

    try {
      recordWorkspaceProjectionDiagnostic({
        stage: 'doc-create:confirmation-started',
        docIds: [docId],
        folderIds: [options.targetFolderId!],
      });
      docEngine?.addDoc(docId, 'doc-create');
      if (docEngine?.waitForUpdated) {
        try {
          await docEngine.waitForUpdated(docId, AbortSignal.timeout(30_000));
          recordWorkspaceProjectionDiagnostic({
            stage: 'doc-create:local-update-durable',
            docIds: [docId],
          });
        } catch (error) {
          logger.warn(
            'Initial target-folder doc create is not locally durable',
            {
              docId,
              targetFolderId: options.targetFolderId,
              error,
            }
          );
          return;
        }
      }
      if (isBackendKnownUnavailable()) {
        return;
      }

      if (docEngine?.flushAndWaitForSynced) {
        try {
          await docEngine.flushAndWaitForSynced(workspaceId);
          recordWorkspaceProjectionDiagnostic({
            stage: 'doc-create:workspace-flush-before-complete',
            docIds: [docId],
          });
          await docEngine.flushAndWaitForSynced(docId);
          recordWorkspaceProjectionDiagnostic({
            stage: 'doc-create:doc-flush-complete',
            docIds: [docId],
          });
          await docEngine.flushAndWaitForSynced(workspaceId);
          recordWorkspaceProjectionDiagnostic({
            stage: 'doc-create:workspace-flush-after-complete',
            docIds: [docId],
          });
        } catch (error) {
          logger.warn(
            'Initial target-folder doc create flush is still pending locally',
            {
              docId,
              targetFolderId: options.targetFolderId,
              error,
            }
          );
          return;
        }
      }

      const confirmed = await this.store.waitForServerConfirmed(docId, {
        timeoutMs: 30_000,
        pollMs: 500,
      });
      recordWorkspaceProjectionDiagnostic({
        stage: 'doc-create:server-confirmation-complete',
        reason: confirmed ? 'confirmed' : 'timed-out',
        docIds: [docId],
      });
      if (confirmed || isBackendKnownUnavailable()) {
        return;
      }

      const backendAvailable = await probeBackendAvailabilityNow();
      if (!backendAvailable) {
        return;
      }

      const confirmedAfterProbe = await this.store.waitForServerConfirmed(
        docId,
        {
          timeoutMs: 5_000,
          pollMs: 250,
        }
      );
      if (confirmedAfterProbe) {
        return;
      }

      logger.warn('Initial target-folder doc create is still pending locally', {
        docId,
        targetFolderId: options.targetFolderId,
      });
    } finally {
      releaseWorkspacePriority?.();
      releaseDocPriority?.();
    }
  }

  async addLinkedDoc(targetDocId: string, linkedDocId: string) {
    const { doc, release } = this.open(targetDocId);
    const disposePriorityLoad = doc.addPriorityLoad(10);
    await doc.waitForSyncReady();
    disposePriorityLoad();
    const text = new Text([
      {
        insert: ' ',
        attributes: {
          reference: {
            type: 'LinkedPage',
            pageId: linkedDocId,
          },
        },
      },
    ] as DeltaInsert<AffineTextAttributes>[]);
    const [frame] = doc.blockSuiteDoc.getBlocksByFlavour('affine:note');
    frame &&
      doc.blockSuiteDoc.addBlock(
        'affine:paragraph' as never, // TODO(eyhn): fix type
        { text },
        frame.id
      );
    release();
  }

  async changeDocTitle(docId: string, newTitle: string) {
    const { doc, release } = this.open(docId);
    const disposePriorityLoad = doc.addPriorityLoad(10);
    try {
      this.store.setDocMeta(docId, {
        title: newTitle,
        updatedDate: Date.now(),
      });
      await doc.waitForSyncReady();
      doc.changeDocTitle(newTitle);
      await this.persistDocTitle(docId, newTitle);
    } finally {
      disposePriorityLoad();
      release();
    }
  }

  reflectDocTitleFromEditor(docId: string, title: string) {
    this.store.setDocMeta(docId, {
      title,
      updatedDate: Date.now(),
    });
  }

  async persistDocTitleFromEditor(docId: string, title: string) {
    await this.persistDocTitle(docId, title);
  }

  private async persistDocTitle(docId: string, title: string) {
    if (this.workspaceService.workspace.flavour === 'local') {
      return;
    }
    if (this.store.isLocallyCreated(docId)) {
      const serverConfirmed = await this.waitForLocalDocServerConfirmed(docId, {
        timeoutMs: 10_000,
        pollMs: 250,
      });
      if (!serverConfirmed) {
        return;
      }
    }
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('No Server');
    }
    await server.gql({
      query: updateDocTitleMutation,
      variables: {
        input: {
          workspaceId: this.workspaceService.workspace.id,
          docId,
          title,
        },
      },
    });
    this.store.confirmDocTitlePersisted(docId, title);
  }

  private async flushLocalDocBeforeMetadataWrite(
    docId: string,
    signal?: AbortSignal
  ): Promise<void> {
    const workspace = this.workspaceService.workspace;
    const docEngine = workspace.engine?.doc;
    docEngine?.addDoc(docId, 'doc-metadata-write');
    if (!docEngine?.flushAndWaitForSynced) {
      return;
    }

    try {
      await docEngine.flushAndWaitForSynced(workspace.id, signal);
      await docEngine.flushAndWaitForSynced(docId, signal);
      await docEngine.flushAndWaitForSynced(workspace.id, signal);
    } catch (error) {
      logger.warn('Local doc metadata write flush is still pending', {
        docId,
        error,
      });
    }
  }

  async duplicate(sourceDocId: string, _targetDocId?: string) {
    const targetDocId = _targetDocId ?? this.createDoc().id;

    // check if source doc is removed
    if (this.list.doc$(sourceDocId).value?.trash$.value) {
      console.warn(
        `Template doc(id: ${sourceDocId}) is removed, skip duplicate`
      );
      return targetDocId;
    }

    const { release: sourceRelease, doc: sourceDoc } = this.open(sourceDocId);
    const { release: targetRelease, doc: targetDoc } = this.open(targetDocId);
    await sourceDoc.waitForSyncReady();

    // duplicate doc content
    let contentDuplicated = false;
    try {
      const sourceBsDoc = this.store.getBlockSuiteDoc(sourceDocId);
      const targetBsDoc = this.store.getBlockSuiteDoc(targetDocId);
      if (!sourceBsDoc) throw new Error('Source doc not found');
      if (!targetBsDoc) throw new Error('Target doc not found');

      // clear the target doc (both surface and note)
      targetBsDoc.root?.children.forEach(child =>
        targetBsDoc.deleteBlock(child)
      );

      const collection = this.store.getBlocksuiteCollection();
      const transformer = new Transformer({
        schema: getStreamLainWorkspaceSchema(),
        blobCRUD: collection.blobSync,
        docCRUD: {
          create: (id: string) => {
            this.createDoc({ id });
            const store = collection.getDoc(id)?.getStore({ id });
            if (!store) {
              throw new Error('Failed to create doc');
            }
            return store;
          },
          get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
          delete: (id: string) => collection.removeDoc(id),
        },
        middlewares: [replaceIdMiddleware(collection.idGenerator)],
      });
      const slice = Slice.fromModels(sourceBsDoc, [
        ...(sourceBsDoc.root?.children ?? []),
      ]);
      const snapshot = transformer.sliceToSnapshot(slice);
      if (!snapshot) {
        throw new Error('Failed to create snapshot');
      }
      await transformer.snapshotToSlice(
        snapshot,
        targetBsDoc,
        targetBsDoc.root?.id
      );
      contentDuplicated = true;
    } catch (e) {
      logger.error('Failed to duplicate doc', {
        sourceDocId,
        targetDocId,
        originalTargetDocId: _targetDocId,
        error: e,
      });
    } finally {
      sourceRelease();
      targetRelease();
    }

    // If content duplication failed, trash the broken target doc
    if (!contentDuplicated) {
      targetDoc.record.moveToTrash();
      return targetDocId;
    }

    // duplicate doc meta
    targetDoc.record.setMeta({
      tags: sourceDoc.meta$.value.tags,
    });

    // duplicate doc title
    const originalTitle = sourceDoc.title$.value;
    const lastDigitRegex = / ?\((\d+)\)$/;
    const match = originalTitle.match(lastDigitRegex);
    const newNumber = match ? parseInt(match[1], 10) + 1 : 1;
    const baseTitle = originalTitle.replace(lastDigitRegex, '');
    const newPageTitle = `${baseTitle} (${newNumber})`;
    targetDoc.changeDocTitle(newPageTitle);

    // duplicate doc properties
    const properties = sourceDoc.getProperties();
    const removedProperties = ['id', 'isTemplate', 'journal'];
    removedProperties.forEach(key => {
      delete properties[key];
    });
    targetDoc.updateProperties(properties);

    return targetDocId;
  }

  /**
   * Duplicate a doc from template
   * @param sourceDocId - the id of the source doc to be duplicated
   * @param _targetDocId - the id of the target doc to be duplicated, if not provided, a new doc will be created
   * @returns the id of the new doc
   */
  async duplicateFromTemplate(
    sourceDocId: string,
    _targetDocId?: string,
    options: Pick<
      DocCreateOptions,
      'createInWorkspaceRoot' | 'targetFolderId' | 'userInitiated'
    > = {}
  ) {
    const createOptions: Pick<
      DocCreateOptions,
      'createInWorkspaceRoot' | 'targetFolderId' | 'userInitiated'
    > = options.targetFolderId
      ? options
      : {
          ...options,
          createInWorkspaceRoot: options.createInWorkspaceRoot ?? true,
        };
    const targetDocId = _targetDocId ?? this.createDoc(createOptions).id;

    // check if source doc is removed
    if (this.list.doc$(sourceDocId).value?.trash$.value) {
      console.warn(
        `Template doc(id: ${sourceDocId}) is removed, skip duplicate`
      );
      return targetDocId;
    }

    const { release: sourceRelease, doc: sourceDoc } = this.open(sourceDocId);
    const { release: targetRelease, doc: targetDoc } = this.open(targetDocId);
    await sourceDoc.waitForSyncReady();

    // duplicate doc content
    let contentDuplicated = false;
    try {
      const sourceBsDoc = this.store.getBlockSuiteDoc(sourceDocId);
      const targetBsDoc = this.store.getBlockSuiteDoc(targetDocId);
      if (!sourceBsDoc) throw new Error('Source doc not found');
      if (!targetBsDoc) throw new Error('Target doc not found');

      // clear the target doc (both surface and note)
      targetBsDoc.root?.children.forEach(child =>
        targetBsDoc.deleteBlock(child)
      );

      const collection = this.store.getBlocksuiteCollection();
      const transformer = new Transformer({
        schema: getStreamLainWorkspaceSchema(),
        blobCRUD: collection.blobSync,
        docCRUD: {
          create: (id: string) => {
            this.createDoc({ id });
            const store = collection.getDoc(id)?.getStore({ id });
            if (!store) {
              throw new Error('Failed to create doc');
            }
            return store;
          },
          get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
          delete: (id: string) => collection.removeDoc(id),
        },
        middlewares: [replaceIdMiddleware(collection.idGenerator)],
      });
      const slice = Slice.fromModels(sourceBsDoc, [
        ...(sourceBsDoc.root?.children ?? []),
      ]);
      const snapshot = transformer.sliceToSnapshot(slice);
      if (!snapshot) {
        throw new Error('Failed to create snapshot');
      }
      await transformer.snapshotToSlice(
        snapshot,
        targetBsDoc,
        targetBsDoc.root?.id
      );
      contentDuplicated = true;
    } catch (e) {
      logger.error('Failed to duplicate doc from template', {
        sourceDocId,
        targetDocId,
        originalTargetDocId: _targetDocId,
        error: e,
      });
    } finally {
      sourceRelease();
      targetRelease();
    }

    // If content duplication failed, trash the broken target doc
    if (!contentDuplicated) {
      targetDoc.record.moveToTrash();
      return targetDocId;
    }

    // duplicate doc properties
    const properties = sourceDoc.getProperties();
    const removedProperties = ['id', 'isTemplate', 'journal'];
    removedProperties.forEach(key => {
      delete properties[key];
    });
    targetDoc.updateProperties(properties);

    return targetDocId;
  }
}
